import React from 'react';
import { Database, Layers, Tag, Gauge, Network, Activity } from 'lucide-react';
import RcTooltip from 'rc-tooltip';
import GraphCanvasShell from './GraphCanvasShell';

const isImpacted = (status) => String(status || '').toLowerCase() === 'impacted';

const HoverTip = ({ text, children, wide = false }) => {
    const label = text == null ? '' : String(text);
    if (!label.trim()) return children;

    return (
        <RcTooltip
            placement="top"
            mouseEnterDelay={0.1}
            classNames={{ root: wide ? 'lin-attr-tip lin-formula-tip' : 'lin-attr-tip' }}
            zIndex={1300}
            overlay={(
                <span
                    style={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        wordBreak: wide ? 'break-word' : 'break-all',
                        whiteSpace: 'pre-wrap',
                        maxWidth: wide ? '420px' : '260px',
                        display: 'block',
                    }}
                >
                    {label}
                </span>
            )}
            arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
        >
            {children}
        </RcTooltip>
    );
};

const renderDatasetRow = (ds, key) => {
    const name = ds?.data_set_name || '—';
    return (
        <HoverTip key={key} text={name}>
            <div className={`c-item${isImpacted(ds?.status) ? ' red' : ''}`}>
                <span className="fi"><Layers size={13} /></span>
                <span className="attr-text">{name}</span>
            </div>
        </HoverTip>
    );
};

const formatSyncTime = (value) => {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (!Number.isNaN(date.valueOf())) {
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
            });
        }
    } catch (e) {
        // fall through
    }
    return String(value);
};

const formatMetricLabel = (metric) => {
    if (metric == null) return '—';
    if (typeof metric === 'string' || typeof metric === 'number') return String(metric);

    const name = metric.metrics_name || metric.name || 'Metric';
    const value = metric.metrics_value != null && metric.metrics_value !== ''
        ? String(metric.metrics_value)
        : null;
    const units = metric.units != null && metric.units !== '' ? String(metric.units) : '';
    const valueText = value != null ? `${value}${units}` : null;

    return valueText ? `${name} ${valueText}` : name;
};

const metricDirectionClass = (metric) => {
    const direction = String(metric?.direction || '').toLowerCase();
    if (direction === 'up' || direction === 'increase') return 'up';
    if (direction === 'down' || direction === 'decrease') return 'dn';
    return '';
};

const renderPipelineMetrics = (pipeline) => {
    const metrics = Array.isArray(pipeline?.metrics) ? pipeline.metrics : [];
    if (!metrics.length) return null;

    return (
        <div className="c-sec">
            <div className="c-sec-lbl">Metrics</div>
            {metrics.map((metric, idx) => {
                const label = formatMetricLabel(metric);
                const value = metric?.metrics_value != null && metric?.metrics_value !== ''
                    ? `${metric.metrics_value}${metric.units || ''}`
                    : null;
                const dirClass = metricDirectionClass(metric);
                const impacted = isImpacted(metric?.status);

                const metricName = metric?.metrics_name || metric?.name || 'Metric';
                return (
                    <HoverTip key={`${pipeline.pipeline_id || pipeline.pipeline_name}-m-${idx}`} text={label}>
                        <div className={`c-item${impacted ? ' red' : ''}`}>
                            <span className="fi"><Activity size={13} /></span>
                            <span className="attr-text">{metricName}</span>
                            {value != null && (
                                <span className={`met${dirClass ? ` ${dirClass}` : ''}`}>{value}</span>
                            )}
                        </div>
                    </HoverTip>
                );
            })}
        </div>
    );
};

export default class LineageModal extends React.Component {
    render() {
        const { show, onClose, row = {}, data = null, loading = false, error = null } = this.props;
        if (!show) return null;

        const kpi = data?.kpi || {};
        const datasource = data?.datasource || {};
        const pipelines = Array.isArray(data?.pipelines) ? data.pipelines : [];
        const stats = data?.stats || {};
        const datasourceDatasets = Array.isArray(datasource.datasets) ? datasource.datasets : [];

        const uniqueDatasetCount = (() => {
            const keys = new Set();
            datasourceDatasets.forEach((ds) => {
                const key = ds?.data_set_id ?? ds?.data_set_name;
                if (key != null && key !== '') keys.add(String(key));
            });
            pipelines.forEach((pipeline) => {
                (Array.isArray(pipeline?.datasets) ? pipeline.datasets : []).forEach((ds) => {
                    const key = ds?.data_set_id ?? ds?.data_set_name;
                    if (key != null && key !== '') keys.add(String(key));
                });
            });
            return keys.size;
        })();

        const mappedAttributes = kpi.mapped_attributes;
        const mappedAttributeCount = Array.isArray(mappedAttributes)
            ? mappedAttributes.length
            : (mappedAttributes?.name ? 1 : 0);

        const datasourceCount = stats.datasource_count ?? datasourceDatasets.length;
        const pipelineCount = stats.pipeline_count ?? pipelines.length;
        const datasetCount = stats.dataset_count ?? uniqueDatasetCount;
        const attributeCount = stats.attribute_count ?? mappedAttributeCount;
        const kpiCount = stats.kpi_count ?? (kpi.kpi_id || kpi.kpi_name ? 1 : 0);

        const kpiName = kpi.kpi_name || row.kpi_name || row.kpiName || 'KPI';
        const formula = kpi.formula || row.formula || '—';
        const attributeName = (Array.isArray(mappedAttributes) ? mappedAttributes[0]?.name : mappedAttributes?.name)
            || row.attributeName
            || row.attribute
            || '—';
        const attributeImpacted = Array.isArray(mappedAttributes)
            ? mappedAttributes.some((attr) => isImpacted(attr?.status))
            : isImpacted(mappedAttributes?.status);
        const driftValue = kpi.drift_value != null && kpi.drift_value !== ''
            ? `${kpi.drift_value}${kpi.drift_unit || '%'}`
            : null;
        const datasourceName = datasource.datasource_name
            || row.datasourceName
            || row.datasource
            || 'Datasource';
        const datasourceType = datasource.type || 'Source';

        const primaryPipeline = pipelines[0] || null;
        const secondaryPipeline = pipelines[1] || null;

        const datasetLabel = (value) => {
            const text = String(value || '').trim();
            if (!text || text === '—') return '';
            if (/\.(xlsx|xls|csv|json|parquet|txt)$/i.test(text)) return '';
            return text;
        };
        const firstDatasetLabel = (values) => {
            for (const value of values) {
                const text = datasetLabel(value);
                if (text) return text;
            }
            return '';
        };
        const mappedDatasetName = firstDatasetLabel(
            (Array.isArray(row.mappedAssets) ? row.mappedAssets : []).map((asset) => asset?.dataset || asset?.data_set_name)
        );
        const sourceDatasetName = firstDatasetLabel(datasourceDatasets.map((ds) => ds?.data_set_name));
        const pipelineDatasetName = firstDatasetLabel(
            pipelines.flatMap((pipeline) => (
                Array.isArray(pipeline?.datasets) ? pipeline.datasets.map((ds) => ds?.data_set_name) : []
            ))
        );
        const previousNodeName = mappedDatasetName
            || sourceDatasetName
            || pipelineDatasetName
            || firstDatasetLabel([row.dataset, row.data_set_name])
            || 'Dataset';

        const hasPipelines = Boolean(primaryPipeline);
        const hasTwoPipelines = Boolean(secondaryPipeline);
        const mappedLeft = hasPipelines ? 730 : 330;
        const kpiLeft = hasPipelines ? 1090 : 690;
        const yOff = hasTwoPipelines ? 0 : 90;
        const mappedTop = 260 + yOff;
        const dsTop = 315 + yOff;
        const kpiTop = (hasPipelines ? 305 : 300) + yOff;
        const pipeTop = (secondaryPipeline ? 55 : 260) + yOff;
        const y = (v) => v + yOff;

        const linCards = [
            { id: 'ds', type: 'source', status: 'normal' },
            ...(hasPipelines ? [{ id: 'p1', type: 'pipe', status: primaryPipeline?.status || 'normal' }] : []),
            ...(hasTwoPipelines ? [{ id: 'p2', type: 'pipe', status: secondaryPipeline?.status || 'normal' }] : []),
            { id: 'attr', type: 'data', status: attributeImpacted ? 'critical' : 'normal' },
            { id: 'kpi', type: 'kpi', status: driftValue ? 'critical' : 'normal' }
        ];

        const linPosMap = {
            ds: { x: 35, y: dsTop, w: 215, h: 280 },
            ...(hasPipelines ? { p1: { x: 330, y: pipeTop, w: 250, h: 210 } } : {}),
            ...(hasTwoPipelines ? { p2: { x: 330, y: y(460), w: 250, h: 210 } } : {}),
            attr: { x: mappedLeft, y: mappedTop, w: 250, h: 210 },
            kpi: { x: kpiLeft, y: kpiTop, w: 230, h: 186 }
        };

        return (
            <GraphCanvasShell
                show={show}
                onClose={onClose}
                defaultTheme="dark"
                canvasW={1320}
                canvasH={780}
                cards={linCards}
                posMap={linPosMap}
                contentKey={`${loading ? 'loading' : 'ready'}-${kpi.kpi_id || kpiName}-${pipelines.length}-${datasourceDatasets.length}`}
                title={(
                    <>
                        <span className="qd-data-lineage-header__prefix k"><Gauge size={18} /> KPI:</span>{' '}
                        <HoverTip text={kpiName}>
                            <span>{kpiName}</span>
                        </HoverTip>
                    </>
                )}
                stats={(
                    <div className="qd-data-lineage-header__stats lin-stats">
                        <span className="qd-data-lineage-header__stat lin-stat">
                            <span className="ic"><Database size={15} /></span>{' '}
                            Datasource: <b>{datasourceCount}</b>
                        </span>
                        <span className="qd-data-lineage-header__stat lin-stat">
                            <span className="ic"><Network size={15} /></span>{' '}
                            Pipelines: <b>{pipelineCount}</b>
                        </span>
                        <span className="qd-data-lineage-header__stat lin-stat">
                            <span className="ic"><Layers size={15} /></span>{' '}
                            Datasets: <b>{datasetCount}</b>
                        </span>
                        <span className="qd-data-lineage-header__stat lin-stat">
                            <span className="ic"><Tag size={15} /></span>{' '}
                            Attributes: <b>{attributeCount}</b>
                        </span>
                        <span className="qd-data-lineage-header__stat lin-stat">
                            <span className="ic"><Gauge size={15} /></span>{' '}
                            KPIs: <b>{kpiCount}</b>
                        </span>
                    </div>
                )}
                headerExtra={(
                    <div className="qd-data-lineage-header__sync lin-sync">
                         Identified Time  <b>{formatSyncTime(data?.last_sync_time)}</b>
                    </div>
                )}
            >
                {loading && (
                    <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-text-white tw-text-[14px]">
                        Loading diagnostics map...
                    </div>
                )}
                {!loading && error && (
                    <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-text-[#F7BAC1] tw-text-[14px] tw-px-6 tw-text-center">
                        {error}
                    </div>
                )}
                {!loading && !error && data && (
                    <>
                        {(() => {
                            const hasPipelines = Boolean(primaryPipeline);
                            const hasTwoPipelines = Boolean(secondaryPipeline);
                            // Layout: with pipelines keep mid column; without pipelines pull mapped card left
                            const mappedLeft = hasPipelines ? 730 : 330;
                            const kpiLeft = hasPipelines ? 1090 : 690;
                            // Sparse graphs (0–1 pipeline) sit high in a 780px canvas — shift down to center
                            const yOff = hasTwoPipelines ? 0 : 90;
                            const mappedTop = 260 + yOff;
                            const dsTop = 315 + yOff;
                            const kpiTop = (hasPipelines ? 305 : 300) + yOff;
                            const pipeTop = (secondaryPipeline ? 55 : 260) + yOff;
                            const y = (v) => v + yOff;

                            const edgeToMappedImpacted = hasPipelines
                                ? (isImpacted(primaryPipeline?.status) || attributeImpacted)
                                : attributeImpacted;

                            return (
                                <>
                                    <svg
                                        className="qd-data-lineage-edges lin-edges"
                                        viewBox="0 0 1320 780"
                                        preserveAspectRatio="none"
                                        style={{ width: '1320px', height: '780px' }}
                                    >
                                        <defs>
                                            <marker id="lin-arrow-grey" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                                                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#CED4DA" />
                                            </marker>
                                            <marker id="lin-arrow-red" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                                                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#FB7185" />
                                            </marker>
                                        </defs>
                                        {hasTwoPipelines ? (
                                            <>
                                                <path className={isImpacted(primaryPipeline?.status) ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={isImpacted(primaryPipeline?.status) ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d="M250 360 C300 360,285 150,330 150" />
                                                <path className={isImpacted(secondaryPipeline?.status) ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={isImpacted(secondaryPipeline?.status) ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d="M250 360 C300 360,285 555,330 555" />
                                                <path className={isImpacted(primaryPipeline?.status) || attributeImpacted ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={isImpacted(primaryPipeline?.status) || attributeImpacted ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d="M580 150 C665 150,650 330,730 330" />
                                                <path className={isImpacted(secondaryPipeline?.status) ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={isImpacted(secondaryPipeline?.status) ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d="M580 555 C665 555,650 330,730 330" />
                                                <path className={attributeImpacted || driftValue ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={attributeImpacted || driftValue ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d="M980 330 C1035 330,1040 355,1090 355" />
                                            </>
                                        ) : hasPipelines ? (
                                            <>
                                                <path className={isImpacted(primaryPipeline?.status) ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={isImpacted(primaryPipeline?.status) ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d={`M250 ${y(360)} C300 ${y(360)},285 ${y(360)},330 ${y(360)}`} />
                                                <path className={edgeToMappedImpacted ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={edgeToMappedImpacted ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d={`M580 ${y(360)} C665 ${y(360)},650 ${y(330)},730 ${y(330)}`} />
                                                <path className={attributeImpacted || driftValue ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={attributeImpacted || driftValue ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d={`M980 ${y(330)} C1035 ${y(330)},1040 ${y(355)},1090 ${y(355)}`} />
                                            </>
                                        ) : (
                                            <>
                                                {/* No pipeline tier — connect datasource → dataset → KPI */}
                                                <path className={attributeImpacted ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={attributeImpacted ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d={`M250 ${y(360)} C285 ${y(360)},300 ${y(330)},330 ${y(330)}`} />
                                                <path className={attributeImpacted || driftValue ? 'qd-data-lineage-edge--red ed-red' : 'qd-data-lineage-edge--grey ed-grey'} markerEnd={attributeImpacted || driftValue ? 'url(#lin-arrow-red)' : 'url(#lin-arrow-grey)'} d={`M580 ${y(330)} C630 ${y(330)},650 ${y(355)},690 ${y(355)}`} />
                                            </>
                                        )}
                                    </svg>

                                    <div className="qd-data-lineage-card card ds" style={{ left: 35, top: dsTop, width: 215, position: 'absolute' }}>
                                        <div className="qd-data-lineage-card__type-tab c-type-tab c-type-tab--source">Data Source</div>
                                        <div className="c-head">
                                            <span className="c-ico"><Database size={13} /></span>
                                            <HoverTip text={datasourceName}>
                                                <span className="c-name">{datasourceName}</span>
                                            </HoverTip>
                                            <span className="c-type">{datasourceType}</span>
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Datasets</div>
                                            {datasourceDatasets.length > 0 ? datasourceDatasets.map((ds) => (
                                                renderDatasetRow(ds, `${ds.data_set_id || ds.data_set_name}`)
                                            )) : (
                                                <div className="c-item"><span className="fi"><Layers size={13} /></span>—</div>
                                            )}
                                        </div>
                                    </div>

                                    {primaryPipeline && (
                                        <div
                                            className={`qd-data-lineage-card card pipe${isImpacted(primaryPipeline.status) ? ' crit st-card-critical' : ''}`}
                                            style={{ left: 330, top: pipeTop, width: 250, position: 'absolute' }}
                                        >
                                            <div className={`qd-data-lineage-card__type-tab c-type-tab c-type-tab--pipe ${isImpacted(primaryPipeline.status) ? 'c-type-tab--critical' : ''}`}>Pipeline</div>
                                            <div className="c-head">
                                                <span className="c-ico"><Network size={13} /></span>
                                                <HoverTip text={primaryPipeline.pipeline_name}>
                                                    <span className="c-name">{primaryPipeline.pipeline_name}</span>
                                                </HoverTip>
                                                {isImpacted(primaryPipeline.status) ? (
                                                    <span className="c-badge"><span className="d"></span>DRIFT</span>
                                                ) : (
                                                    <span className="c-type">Pipeline</span>
                                                )}
                                            </div>
                                            <div className="c-sec">
                                                <div className="c-sec-lbl">Datasets</div>
                                                {(Array.isArray(primaryPipeline.datasets) ? primaryPipeline.datasets : []).map((ds, index) => (
                                                    renderDatasetRow(ds, `${primaryPipeline.pipeline_id}-${ds.data_set_name}-${index}`)
                                                ))}
                                            </div>
                                            {renderPipelineMetrics(primaryPipeline)}
                                        </div>
                                    )}

                                    {secondaryPipeline && (
                                        <div
                                            className={`qd-data-lineage-card card pipe${isImpacted(secondaryPipeline.status) ? ' crit st-card-critical' : ''}`}
                                            style={{ left: 330, top: 445, width: 250, position: 'absolute' }}
                                        >
                                            <div className={`qd-data-lineage-card__type-tab c-type-tab c-type-tab--pipe ${isImpacted(secondaryPipeline.status) ? 'c-type-tab--critical' : ''}`}>Pipeline</div>
                                            <div className="c-head">
                                                <span className="c-ico"><Network size={13} /></span>
                                                <HoverTip text={secondaryPipeline.pipeline_name}>
                                                    <span className="c-name">{secondaryPipeline.pipeline_name}</span>
                                                </HoverTip>
                                                {isImpacted(secondaryPipeline.status) ? (
                                                    <span className="c-badge"><span className="d"></span>DRIFT</span>
                                                ) : (
                                                    <span className="c-type">Pipeline</span>
                                                )}
                                            </div>
                                            <div className="c-sec">
                                                <div className="c-sec-lbl">Datasets</div>
                                                {(Array.isArray(secondaryPipeline.datasets) ? secondaryPipeline.datasets : []).map((ds, index) => (
                                                    renderDatasetRow(ds, `${secondaryPipeline.pipeline_id}-${ds.data_set_name}-${index}`)
                                                ))}
                                            </div>
                                            {renderPipelineMetrics(secondaryPipeline)}
                                        </div>
                                    )}

                                    <div
                                        className={`qd-data-lineage-card card data${attributeImpacted ? ' crit st-card-critical' : ''}`}
                                        style={{ left: mappedLeft, top: mappedTop, width: 250, position: 'absolute' }}
                                    >
                                        <div className={`qd-data-lineage-card__type-tab c-type-tab c-type-tab--data ${attributeImpacted ? 'c-type-tab--critical' : ''}`}>Dataset</div>
                                        <div className="c-head">
                                            <span className="c-ico"><Layers size={13} /></span>
                                            <HoverTip text={previousNodeName}>
                                                <span className="c-name">{previousNodeName}</span>
                                            </HoverTip>
                                            {attributeImpacted ? (
                                                <span className="c-badge"><span className="d"></span>IMPACTED</span>
                                            ) : (
                                                <span className="c-type">Mapped</span>
                                            )}
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Attributes</div>
                                            {Array.isArray(mappedAttributes) && mappedAttributes.length > 0 ? (
                                                mappedAttributes.map((attr, index) => {
                                                    const name = attr?.name || attr;
                                                    return (
                                                        <HoverTip key={`${name}-${index}`} text={name}>
                                                            <div className={`c-item${isImpacted(attr?.status) ? ' red' : ''}`}>
                                                                <span className="fi"><Tag size={13} /></span>
                                                                <span className="attr-text">{name}</span>
                                                            </div>
                                                        </HoverTip>
                                                    );
                                                })
                                            ) : (
                                                <HoverTip text={attributeName}>
                                                    <div className={`c-item${attributeImpacted ? ' red' : ''}`}>
                                                        <span className="fi"><Tag size={13} /></span>
                                                        <span className="attr-text">{attributeName}</span>
                                                    </div>
                                                </HoverTip>
                                            )}
                                        </div>
                                        {driftValue && (
                                            <div className="c-sec">
                                                <div className="c-sec-lbl">Metrics</div>
                                                <HoverTip text={`Drift ${driftValue}`}>
                                                    <div className="c-item red">
                                                        <span className="fi"><Activity size={13} /></span>
                                                        <span className="attr-text">Drift</span>
                                                        <span className="met dn">{driftValue}</span>
                                                    </div>
                                                </HoverTip>
                                            </div>
                                        )}
                                    </div>

                                    <div className="qd-data-lineage-card card kpi crit focus" style={{ left: kpiLeft, top: kpiTop, width: 230, position: 'absolute' }}>
                                        <div className={`qd-data-lineage-card__type-tab c-type-tab c-type-tab--kpi ${driftValue ? 'c-type-tab--critical' : ''}`}>KPI Metric</div>
                                        <div className="c-head">
                                            <span className="c-ico"><Gauge size={13} /></span>
                                            <HoverTip text={kpiName}>
                                                <span className="c-name">{kpiName}</span>
                                            </HoverTip>
                                            {driftValue ? (
                                                <span className="c-badge"><span className="d"></span>{driftValue}</span>
                                            ) : null}
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Main KPI · mapped attribute</div>
                                            <HoverTip text={attributeName}>
                                                <div className={`c-item${attributeImpacted ? ' red' : ''}`}>
                                                    <span className="fi"><Tag size={13} /></span>
                                                    <span className="attr-text">{attributeName}</span>
                                                </div>
                                            </HoverTip>
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Formula</div>
                                            <HoverTip text={formula} wide>
                                                <div className="c-formula">{formula}</div>
                                            </HoverTip>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </>
                )}
            </GraphCanvasShell>
        );
    }
}
